import { useState } from "react";
import { Brain } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AnalysisErrorBoundary } from "@/components/AnalysisErrorBoundary";
import { toNumber, toText, toTextList } from "@/lib/analysisSafe";

interface JobAnalysisTabProps {
  jobId: string;
  analysis: any;
}

const JobAnalysisContent = ({ jobId, analysis }: JobAnalysisTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation();

  // No analysis data available
  if (!analysis || typeof analysis !== "object" || Object.keys(analysis).length === 0) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-xl flex items-center">
            <Brain className="mr-2 h-5 w-5 text-blue-500" />
            {t("jobs.analysis.title", "Job Analysis")}
          </CardTitle>
          <CardDescription>
            {t("jobs.analysis.noAI", "No AI analysis available for this job yet.")}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Helper function to render a metric with explanation
  const renderMetric = (title: string, value: any, explanation: string) => {
    if (value === undefined || value === null) return null;

    const numericValue = toNumber(value);

    return (
      <div className="space-y-1.5">
        <div className="flex justify-between text-sm">
          <span>{title}</span>
          {numericValue !== null && <span>{Math.round(numericValue * 100)}%</span>}
        </div>
        {numericValue !== null && (
          <Progress value={Math.min(Math.max(numericValue * 100, 0), 100)} className="h-2" />
        )}
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{toText(explanation)}</p>
      </div>
    );
  };

  // Helper function to render a list of items
  const renderList = (title: string, items: unknown, explanation: string) => {
    const safeItems = toTextList(items);
    if (safeItems.length === 0) return null;

    return (
      <div className="space-y-2">
        <h4 className="font-medium text-gray-900 dark:text-gray-100">{title}</h4>
        <div className="flex flex-wrap gap-1.5">
          {safeItems.map((item, index) => (
            <Badge key={index} variant="secondary">{item}</Badge>
          ))}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{toText(explanation)}</p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-xl flex items-center">
            <Brain className="mr-2 h-5 w-5 text-blue-500" />
            {t("jobs.analysis.aiTitle", "AI Job Analysis")}
          </CardTitle>
          <CardDescription>
            {t("jobs.analysis.insights", "Insights extracted from the job description by AI")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Skills and Requirements */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {analysis.skills_required && (
              <div className="space-y-3">
                {renderList(
                  t("jobs.analysis.requiredSkills", "Required Skills"),
                  analysis.skills_required["Extracted Value"],
                  analysis.skills_required.Explanation
                )}
              </div>
            )}

            {analysis.technologies_handled && (
              <div className="space-y-3">
                {renderList(
                  t("jobs.analysis.technologies", "Technologies"),
                  analysis.technologies_handled["Extracted Value"],
                  analysis.technologies_handled.Explanation
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Work Environment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t("jobs.analysis.workEnvironment", "Work Environment")}</h4>
              
              {renderMetric(
                t("jobs.analysis.workLifeBalance", "Work-Life Balance"),
                analysis.work_life_balance && analysis.work_life_balance["Extracted Value"],
                analysis.work_life_balance && analysis.work_life_balance.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.remoteWork", "Remote Work"),
                analysis.remote_work_policy && analysis.remote_work_policy["Extracted Value"],
                analysis.remote_work_policy && analysis.remote_work_policy.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.hybridWork", "Hybrid Work Ratio"),
                analysis.hybrid_work_ratio && analysis.hybrid_work_ratio["Extracted Value"],
                analysis.hybrid_work_ratio && analysis.hybrid_work_ratio.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.asynchronousWork", "Asynchronous Work"),
                analysis.asynchronous_work && analysis.asynchronous_work["Extracted Value"],
                analysis.asynchronous_work && analysis.asynchronous_work.Explanation
              )}
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t("jobs.analysis.culture", "Company Culture")}</h4>
              
              {renderMetric(
                t("jobs.analysis.innovation", "Innovation Culture"),
                analysis.innovation_culture && analysis.innovation_culture["Extracted Value"],
                analysis.innovation_culture && analysis.innovation_culture.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.recognition", "Recognition Level"),
                analysis.recognition_level && analysis.recognition_level["Extracted Value"],
                analysis.recognition_level && analysis.recognition_level.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.hierarchy", "Hierarchy Structure"),
                analysis.hierarchy_structure && analysis.hierarchy_structure["Extracted Value"],
                analysis.hierarchy_structure && analysis.hierarchy_structure.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.micromanagement", "Micromanagement Level"),
                analysis.micromanagement_level && analysis.micromanagement_level["Extracted Value"],
                analysis.micromanagement_level && analysis.micromanagement_level.Explanation
              )}
            </div>
          </div>

          <Separator />

          {/* Work Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t("jobs.analysis.workStyle", "Work Style")}</h4>
              
              {renderMetric(
                t("jobs.analysis.collaboration", "Collaboration Intensity"),
                analysis.collaboration_intensity && analysis.collaboration_intensity["Extracted Value"],
                analysis.collaboration_intensity && analysis.collaboration_intensity.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.meetingFrequency", "Meeting Frequency"),
                analysis.meeting_frequency && analysis.meeting_frequency["Extracted Value"],
                analysis.meeting_frequency && analysis.meeting_frequency.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.communication", "Communication Formality"),
                analysis.communication_formality && analysis.communication_formality["Extracted Value"],
                analysis.communication_formality && analysis.communication_formality.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.decisionMaking", "Decision Making Autonomy"),
                analysis.decision_making_autonomy && analysis.decision_making_autonomy["Extracted Value"],
                analysis.decision_making_autonomy && analysis.decision_making_autonomy.Explanation
              )}
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t("jobs.analysis.expectations", "Expectations")}</h4>
              
              {renderMetric(
                t("jobs.analysis.stressLevel", "Stress Level"),
                analysis.stress_level && analysis.stress_level["Extracted Value"],
                analysis.stress_level && analysis.stress_level.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.urgency", "Urgency Expectation"),
                analysis.urgency_expectation && analysis.urgency_expectation["Extracted Value"],
                analysis.urgency_expectation && analysis.urgency_expectation.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.availability", "Availability Expectation"),
                analysis.availability_expectation && analysis.availability_expectation["Extracted Value"],
                analysis.availability_expectation && analysis.availability_expectation.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.performance", "Performance Tracking"),
                analysis.performance_tracking && analysis.performance_tracking["Extracted Value"],
                analysis.performance_tracking && analysis.performance_tracking.Explanation
              )}
            </div>
          </div>

          <Separator />

          {/* Career & Compensation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t("jobs.analysis.career", "Career Growth")}</h4>
              
              {renderMetric(
                t("jobs.analysis.promotionSpeed", "Promotion Speed"),
                analysis.promotion_speed && analysis.promotion_speed["Extracted Value"],
                analysis.promotion_speed && analysis.promotion_speed.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.riskTolerance", "Risk Tolerance"),
                analysis.risk_tolerance && analysis.risk_tolerance["Extracted Value"],
                analysis.risk_tolerance && analysis.risk_tolerance.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.changeAdaptability", "Change Adaptability"),
                analysis.change_adaptability && analysis.change_adaptability["Extracted Value"],
                analysis.change_adaptability && analysis.change_adaptability.Explanation
              )}

              {renderMetric(
                t("jobs.analysis.technologyAdoption", "Technology Adoption Speed"),
                analysis.technology_adoption_speed && analysis.technology_adoption_speed["Extracted Value"],
                analysis.technology_adoption_speed && analysis.technology_adoption_speed.Explanation
              )}
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t("jobs.analysis.compensation", "Compensation")}</h4>
              
              {analysis.salary_range && (
                <div className="space-y-1.5">
                  <h5 className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.analysis.salaryRange", "Salary Range")}</h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{toText(analysis.salary_range["Extracted Value"])}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{toText(analysis.salary_range.Explanation)}</p>
                </div>
              )}

              {renderMetric(
                t("jobs.analysis.bonusStructure", "Bonus Structure"),
                analysis.bonus_structure && analysis.bonus_structure["Extracted Value"],
                analysis.bonus_structure && analysis.bonus_structure.Explanation
              )}
            </div>
          </div>

          {/* Hidden Risks */}
          {analysis.hidden_risks && (
            <div className="mt-6">
              <h4 className="font-medium text-gray-900 mb-3">{t("jobs.analysis.hiddenRisks", "Hidden Risks")}</h4>
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-sm">
                <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">{toText(analysis.hidden_risks.Explanation)}</p>
                {toTextList(analysis.hidden_risks["Extracted Value"]).length > 0 && (
                  <ul className="list-disc list-inside space-y-1">
                    {toTextList(analysis.hidden_risks["Extracted Value"]).map((risk, index) => (
                      <li key={index} className="text-sm text-amber-700 dark:text-amber-400">{risk}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const JobAnalysisTab = (props: JobAnalysisTabProps) => {
  const { t } = useTranslation();

  return (
    <AnalysisErrorBoundary
      fallback={
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl flex items-center">
              <Brain className="mr-2 h-5 w-5 text-red-500" />
              {t("jobs.analysis.error", "Analysis Error")}
            </CardTitle>
            <CardDescription>
              {t("jobs.analysis.errorMessage", "The AI analysis could not be displayed because its format is unexpected. Try re-running the analysis.")}
            </CardDescription>
          </CardHeader>
        </Card>
      }
    >
      <JobAnalysisContent {...props} />
    </AnalysisErrorBoundary>
  );
};